import ExitIntentPopup from "@/components/Alternative/v2/ExitIntentPopup";
import CookiesConsentBanner from "@/components/organisms/CookiesConsentBanner";
import { I18nResourcesProvider } from "@/hooks/useI18nContext";
import { IS_TIMEZONE_INDIA } from "@/shared/constants/timezone.constants";
import { CookiesCopiesType, cookiesCopies_en } from "@/shared/data/common.data";
import { cookiesCopies_kr } from "@/shared/data/common.data.kr";
import Amplitude from "@/shared/services/amplitude.service";
import { useDataStore } from "@/shared/store/useDataStore";
import useModalStore, { AuthModalTypeEnum } from "@/shared/store/useModalStore";
import useSignUpStore from "@/shared/store/useSignUpStore";
import { getCurrentURL } from "@/shared/utils/commons";
import { isBrowser } from "@/shared/utils/dom";
import { IpInfoAPI } from "@/shared/utils/ip.utils";
import { getCalendlyLink } from "@/shared/utils/route.util";
import {
  appendQueryParamsToLocalStorage,
  setLocal,
} from "@/shared/utils/storage.util";
import { getQueryObjFromURL, isRunningOnRootHost } from "@/shared/utils/url.util";
import { extractParamsFromObj, getUTMFieldsFromLocalStorage } from "@/shared/utils/utm.util";
import "@/styles/globals.css";
import { config } from "@fortawesome/fontawesome-svg-core"; // 👈
import "@fortawesome/fontawesome-svg-core/styles.css"; // 👈
import { isNullOrUndefined } from "@graphy/ui";
import { CountryCode } from "libphonenumber-js/types";
import { isEmpty } from "lodash";
import type { AppContext, AppInitialProps, AppProps } from "next/app";
import App from "next/app";
import dynamic from "next/dynamic";
import { Inter } from "next/font/google";
import localFont from "next/font/local";
import { Router, useRouter } from "next/router";
import { parseCookies, setCookie } from "nookies";
import NProgress from "nprogress";
import { useCallback, useEffect, useRef, useState } from "react";
import { getCountryCallingCode } from "react-phone-number-input";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

config.autoAddCss = false;
const CustomScripts = dynamic(
  () => import("@/components/organisms/CustomScripts")
);
const instrumentSans = localFont({
  src: "../assets/fonts/InstrumentSans-VariableFont.ttf",
  variable: "--font-instrument-sans",
});

// Inter was previously loaded as a render-blocking <link> in _document.tsx.
// next/font self-hosts the file, generates @font-face with display:swap,
// and preloads only the weights we use — fixing FCP/LCP without losing
// the font (~120 components reference `font-inter` via Tailwind).
const inter = Inter({
  subsets: ["latin"],
  weight: ["400", "500", "600", "700"],
  variable: "--font-inter",
  display: "swap",
});

type AppOwnProps = {
  locale: "en" | "kr" | "us";
  resources: {
    cookiesCopies?: CookiesCopiesType;
  };
};

function GraphyApp({
  Component,
  pageProps,
  locale,
  resources,
}: AppProps & AppOwnProps) {
  const { setCountryCode, setCurrencySymbol } = useDataStore();
  const {
    setCountryCode: setCountryCodeForSignup,
    countryCode,
    setCallingCode,
  } = useSignUpStore();
  const router = useRouter();
  const initialPageViewSent = useRef(false);

  const { authModalType } = useModalStore();

  const setCountryCodeAndCurrency = useCallback(
    (countryCode: CountryCode, updateCookie: boolean = true) => {
      setCountryCode(countryCode);
      setCountryCodeForSignup(countryCode);
      setCurrencySymbol(countryCode === "IN" ? "₹" : "$");
      const callingCode = getCountryCallingCode(countryCode as any);
      setCallingCode(callingCode);
      if (updateCookie) {
        setCookie(null, "countryCode", countryCode, {
          maxAge: 24 * 60 * 60,
          path: "/",
        });
      }
    },
    [setCountryCode, setCurrencySymbol, setCountryCodeForSignup, setCallingCode]
  );

  const getIpInfo = useCallback(async () => {
    const cookies = parseCookies();

    //reset for cookies in case not KR
    if (cookies.countryCode && cookies.countryCode !== "KR") {
      setCountryCodeAndCurrency(cookies.countryCode as CountryCode, false);
      return;
    }
    try {
      const res = await new IpInfoAPI().getIpInfo();

      if (!isNullOrUndefined(res)) {
        // set as per IP
        if (res?.success) {
          setCountryCodeAndCurrency(res?.country?.toUpperCase() as CountryCode);
        } else {
          setCountryCodeAndCurrency(IS_TIMEZONE_INDIA ? "IN" : "US");
        }
      }
    } catch (err) {
      console.error(err);
      setCountryCodeAndCurrency(IS_TIMEZONE_INDIA ? "IN" : "US");
    }
  }, [setCountryCodeAndCurrency]);

  useEffect(() => {
    // Initialize country code based on IP
    getIpInfo();
  }, [getIpInfo]);

  useEffect(() => {
    const cookies = parseCookies();
    const hasCookieCountryCode = cookies.countryCode;
    // Track initial page view only once
    if (isBrowser && !initialPageViewSent.current && hasCookieCountryCode) {
      initialPageViewSent.current = true;
      
      const amplitude = new Amplitude();
      const utmParams = getUTMFieldsFromLocalStorage();
      
      amplitude.sendEvent({
        type: "page_viewed",
        properties: {
          page_url: getCurrentURL(),
          page_path: window.location.pathname,
          page_title: document.title,
          referrer: document.referrer,
          visitor_id: getVisitorId(),
          utm_params: utmParams,
          signup_version: cookies.signupVersion
        } as { [key: string]: any },
      });
    }

    Router.events.on("routeChangeStart", () => NProgress.start());
    Router.events.on("routeChangeError", () => NProgress.done());

    const handleRouteChangeComplete = () => {
      // ReactGA.pageview(window.location.pathname + window.location.search);
      if (isBrowser) {
        const amplitude = new Amplitude();
        const utmParams = getUTMFieldsFromLocalStorage();
        
        amplitude.sendEvent({
          type: "page_viewed",
          properties: {
            page_url: getCurrentURL(),
            page_path: window.location.pathname,
            page_title: document.title,
            referrer: document.referrer,
            visitor_id: getVisitorId(),
            utm_params: utmParams,
            signup_version: cookies.signupVersion,
          } as { [key: string]: any },
        });
      }
      NProgress.done();
    }

    Router.events.on("routeChangeComplete", handleRouteChangeComplete);

    return () => {
      Router.events.off("routeChangeStart", () => NProgress.start());
      Router.events.off("routeChangeError", () => NProgress.done());
      Router.events.off("routeChangeComplete", handleRouteChangeComplete);
    }
  }, []);

  const [isQueryParamsSet, setQueryParams] = useState(false);

  useEffect(() => {
    if (isRunningOnRootHost()) {
      const queryParams = getQueryObjFromURL();

      const { utmFields, gref, otherParams } =
        extractParamsFromObj(queryParams);

      if (!isEmpty(utmFields)) {
        // If new utmFields are found, remove all old ones and save only the new ones:
        setLocal("utmFields", utmFields as { [key: string]: string });
      }

      if (!isEmpty(otherParams)) {
        // Keep the old and new values, while overwriting with new value for existing keys:
        appendQueryParamsToLocalStorage(
          otherParams as { [key: string]: string }
        );
      }
      setQueryParams(true);
    } // handles all cases for saving params
  }, [router]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    //presence of gref redirects to signup
    if (params.get("gref")) {
      setLocal("gref", params.get("gref") as string);
      // if (!window.location.pathname.includes("/signup")) {
      //   router.push("/signup");
      // }
    }
  }, [router]);

  const showCookiesBanner = () => {
    let showBanner = true;
    if (countryCode === "IN") {
      showBanner = false;
    }
    if (authModalType === AuthModalTypeEnum.SignUp) {
      showBanner = false;
    }
    return showBanner;
  };

  // Utility function to get or generate visitor ID
  const getVisitorId = () => {
    let visitorId = localStorage.getItem('graphy_visitor_id');
    if (!visitorId) {
      // Generate truly unique ID for each new visitor
      const timestamp = Date.now();
      const random = Math.random().toString(36).substring(2, 15);
      visitorId = `graphy_${timestamp}_${random}`;
      localStorage.setItem('graphy_visitor_id', visitorId);
    }
    return visitorId;
  };

  return (
    <I18nResourcesProvider locale={locale} resources={resources}>
      <main className={`${inter.variable} ${instrumentSans.variable} scroll-smooth font-sans`}>
        <CustomScripts />
        <Component {...pageProps} locale={locale} />
        <ToastContainer
          position="top-right"
          autoClose={8000}
          hideProgressBar={true}
          newestOnTop={false}
          draggable={false}
          // pauseOnVisibilityChange
          closeOnClick
          pauseOnHover
        />
        {showCookiesBanner() ? <CookiesConsentBanner /> : null}
        {router.pathname !== "/" && router.locale === "us" && (
          <ExitIntentPopup calendlyUrl={getCalendlyLink(countryCode)} />
        )}
      </main>
    </I18nResourcesProvider>
  );
}

GraphyApp.getInitialProps = async (
  context: AppContext
): Promise<AppOwnProps & AppInitialProps> => {
  // console.log(">>>>>>> context", context);

  const ctx = await App.getInitialProps(context);

  const { locale } = context.router;

  const cookiesCopies = {
    en: cookiesCopies_en,
    us: cookiesCopies_en,
    kr: cookiesCopies_kr,
  }[(locale as "en" | "kr" | "us") ?? "en"];

  // console.log(">>>>>>> ctx", ctx);
  return {
    ...ctx,
    locale: (locale as "en" | "kr" | "us") ?? "en",
    resources: { cookiesCopies },
  };
};

export default GraphyApp;
